import { defineStore } from "pinia"
import { api, type Cart } from "../lib/api"
import { byLine, byVariant, lineBefore, unitsRemoved, type LineMatch } from "../lib/cartremoval"
import { track } from "../lib/tracking"

// remove_from_cart fires from the four call sites below, which are the only
// places anything ever leaves a cart. The RULE for how many units left lives in
// lib/cartremoval, as pure functions over the carts either side of the call — it
// is the part worth testing, and it is testable without a store.
function trackRemoval(before: Cart | null, after: Cart | null, match: LineMatch) {
  const line = lineBefore(before, match)
  const removed = unitsRemoved(before, after, match)
  if (!line || removed <= 0) return
  track({ name: "remove_from_cart", line, quantity: removed })
}

const TOKEN_KEY = `dk:cart:${location.host}`
const NOTE_KEY = `dk:cart:note:${location.host}`
const CODE_KEY = `dk:cart:code:${location.host}`
const ARMS_KEY = `dk:cart:arms:${location.host}`

type CartArms = NonNullable<Cart["experiments"]>

// The cart's price-test arms, remembered beside its token (plan D6): the cart is
// priced under the session that filled it, so a shopper who comes back in a new
// session would otherwise paint this session's arm and then the cart's when
// GET /cart lands — a price that changes in front of them. Read at startup so
// lib/displayPrice has the cart's arm before the network; the server's answer
// replaces it.
function readArms(): CartArms {
  try {
    const a = JSON.parse(localStorage.getItem(ARMS_KEY) ?? "[]")
    return Array.isArray(a) ? a : []
  } catch {
    return []
  }
}

function writeArms(a: CartArms) {
  try {
    if (a.length) localStorage.setItem(ARMS_KEY, JSON.stringify(a))
    else localStorage.removeItem(ARMS_KEY)
  } catch {
    /* the cart response still carries them */
  }
}

// Cart state. The opaque cart token is persisted to localStorage; every mutation
// returns the authoritative server cart (prices are always server-computed).
export const useCartStore = defineStore("cart", {
  state: () => ({
    token: localStorage.getItem(TOKEN_KEY) ?? "",
    cart: null as Cart | null,
    busy: false,
    open: false,
    // Buyer's order note + discount code entered in the cart drawer, forwarded to
    // checkout. Persisted so they survive reloads/SPA nav before checkout.
    note: localStorage.getItem(NOTE_KEY) ?? "",
    discountCode: localStorage.getItem(CODE_KEY) ?? "",
    arms: readArms(),
  }),
  getters: {
    count: (s) => s.cart?.item_count ?? 0,
    lines: (s) => s.cart?.lines ?? [],
    bundles: (s) => s.cart?.bundles ?? [],
    giftLine: (s) => s.cart?.gift ?? null,
    subtotal: (s) => s.cart?.subtotal_cents ?? 0,
  },
  actions: {
    persist(cart: Cart) {
      this.cart = cart
      this.setArms(cart.experiments ?? [])
      if (cart.token && cart.token !== this.token) {
        this.token = cart.token
        localStorage.setItem(TOKEN_KEY, cart.token)
      }
    },
    // Adopt a cart by token — the abandoned-cart recovery link (/cart?recover=…).
    // It replaces whatever this browser had, on purpose: the shopper clicked a
    // reminder about THAT basket, and it has to work on a device that has no cart
    // of its own (which is the whole point of mailing a link).
    setArms(a: CartArms) {
      if (JSON.stringify(a) === JSON.stringify(this.arms)) return
      this.arms = a
      writeArms(a)
    },
    adopt(token: string) {
      if (!token || token === this.token) return
      this.token = token
      this.cart = null
      this.setArms([])
      try {
        localStorage.setItem(TOKEN_KEY, token)
      } catch {
        /* the token still works for this page load */
      }
    },
    async load() {
      if (!this.token) return
      try {
        this.persist((await api.getCart(this.token)).cart)
      } catch {
        /* empty/no cart yet — and no arms to paint from */
        this.setArms([])
      }
    },
    openCart() {
      this.open = true
    },
    closeCart() {
      this.open = false
    },
    setNote(v: string) {
      this.note = v
      localStorage.setItem(NOTE_KEY, v)
    },
    setDiscountCode(v: string) {
      this.discountCode = v
      localStorage.setItem(CODE_KEY, v)
    },
    // `reveal` opts out of popping the drawer open. It exists for the cart add-on
    // toggles, which are pressed from INSIDE the cart: on the drawer the reveal is
    // a no-op, but on /cart it would slide the drawer over the page the buyer is
    // already using. The tracking event still fires — an add-on is a real purchase.
    async add(
      variantId: number,
      quantity = 1,
      properties?: Record<string, string>,
      opts: { reveal?: boolean; designId?: number } = {},
    ) {
      this.busy = true
      try {
        this.persist((await api.addItem(this.token, variantId, quantity, properties, opts.designId)).cart)
        // Every add-to-cart in the app funnels through here, so this is the one
        // place the event needs to fire — and the server cart supplies the title
        // and price the ad platforms want.
        if (this.cart) track({ name: "add_to_cart", cart: this.cart, variantIds: [variantId], quantity })
        if (opts.reveal !== false) this.open = true // reveal the slide-out drawer on add
      } finally {
        this.busy = false
      }
    },
    async setQty(variantId: number, quantity: number) {
      if (quantity <= 0) return this.remove(variantId)
      const before = this.cart
      this.persist((await api.setItem(this.token, variantId, quantity)).cart)
      trackRemoval(before, this.cart, byVariant(variantId))
    },
    async remove(variantId: number) {
      const before = this.cart
      this.persist((await api.removeItem(this.token, variantId)).cart)
      trackRemoval(before, this.cart, byVariant(variantId))
    },
    // Line-keyed: the drawer uses these so a variant that occupies several lines
    // (different engraving text) updates the line the buyer actually clicked.
    async setLineQty(lineId: number, quantity: number) {
      if (quantity <= 0) return this.removeLine(lineId)
      const before = this.cart
      this.persist((await api.setLine(this.token, lineId, quantity)).cart)
      trackRemoval(before, this.cart, byLine(lineId))
    },
    async removeLine(lineId: number) {
      const before = this.cart
      this.persist((await api.removeLine(this.token, lineId)).cart)
      trackRemoval(before, this.cart, byLine(lineId))
    },
    async addBundle(bundleId: number, productId: number, tierId: number, variantIds: number[]) {
      this.busy = true
      try {
        this.persist((await api.addBundle(this.token, bundleId, productId, tierId, variantIds)).cart)
        // One event covering every unit of the bundle (the slot variant ids).
        if (this.cart) track({ name: "add_to_cart", cart: this.cart, variantIds, quantity: 1 })
        this.open = true
      } finally {
        this.busy = false
      }
    },
    /** "Remove" on a bundle header — drops every line of that pool at once. */
    async removeBundle(bundleId: number, productId: number) {
      this.persist((await api.removeBundle(this.token, bundleId, productId)).cart)
    },
  },
})
